import { useCallback, useState, type ReactNode } from 'react';

/**
 * Field-level form errors (QA #68). Every form marks the offending input —
 * red outline plus a Chinese hint under it that says what is wrong and how to
 * enter it — both for its own pre-flight checks and for the backend's 400
 * VALIDATION_ERROR `fields` map (QA #69, pkg/response/validation.go). The
 * page-level red Alert is only for problems that belong to no field.
 *
 * Keys are the API's JSON field keys (snake_case); each TextField / Select
 * carries the same key as its `name`, which is also how the first errored
 * field is focused after a failed submit.
 */
export type FieldErrors = Record<string, string>;

const GENERIC_VALIDATION = '輸入資料有誤，請檢查標示的欄位';
const GENERIC_FIELD = '這個欄位的內容有誤，請重新輸入';
const GENERIC_FAILURE = '操作失敗，請稍後再試';

/**
 * Chinese wording for domain codes whose backend `message` is English (the
 * auth / users handlers still answer with raw gin.H text). Codes the backend
 * already phrases in Chinese (PASSWORD_*, USERNAME_INVALID) keep their
 * message; these are only the fallback.
 */
const CODE_MESSAGES: Record<string, string> = {
  INVALID_OLD_PASSWORD: '目前密碼不正確，請重新輸入',
  INVALID_CREDENTIALS: '帳號或密碼不正確，請重新輸入',
  TOO_MANY_ATTEMPTS: '嘗試次數過多，請稍後再試',
  INVALID_OTP: '驗證碼不正確，請重新輸入信件中的 6 位數字',
  OTP_EXPIRED: '驗證碼已過期，請按「重新發送驗證碼」再輸入新的驗證碼',
  PASSWORD_TOO_SHORT: '密碼長度不足，請依照欄位說明的規則重新輸入',
  PASSWORD_TOO_SIMPLE: '密碼不夠複雜，請依照欄位說明的規則重新輸入',
  PASSWORD_CONTAINS_USERNAME: '密碼不可包含帳號名稱，請換一組',
  PASSWORD_SAME_AS_OLD: '新密碼不可與目前的密碼相同，請換一組',
  USERNAME_INVALID: '使用者名稱不符合規則，請依照欄位說明重新輸入',
  USERNAME_EXISTS: '這個使用者名稱已被使用，請換一個',
  EMAIL_EXISTS: '這個電子信箱已被其他帳號使用，請換一個',
  USER_EXISTS: '這個電子信箱已被其他帳號使用，請換一個',
  GROUP_EXISTS: '這個群組名稱已存在，請換一個',
  ALERT_EVENT_INVALID: '事件類型不正確，請重新勾選',
};

const CJK = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/;

/**
 * The message as a person may see it, or '' when it is validator output
 * (`Key: 'Req.Host' Error:Field validation …`), a struct dump or an English
 * sentence — those never reach the form (QA #4 / #68).
 */
function readable(text: unknown): string {
  if (typeof text !== 'string') return '';
  const t = text.trim();
  if (!t) return '';
  if (/Key:|Error:Field validation|failed on the '/.test(t)) return '';
  return CJK.test(t) ? t : '';
}

/**
 * Splits a rejected request into per-field hints and a general message.
 *
 * - `err.fields` (400 VALIDATION_ERROR, QA #69) lands on those fields as-is;
 *   key `_` (the request itself could not be read) becomes `general`.
 * - Otherwise a code listed in `codeToField` puts `err.message` (or the
 *   Chinese fallback for that code) under that field.
 * - Anything else is `general`: the message when it is Chinese, else the
 *   code's fallback, else `fallback`.
 *
 * `codeToField` may also alias a backend field key to the input the form
 * shows it on (e.g. `primary_group_id` → `group_ids`).
 */
export function fieldErrorsFromApi(
  err: unknown,
  codeToField: Record<string, string> = {},
  fallback: string = GENERIC_FAILURE,
): { fields: FieldErrors; general: string } {
  const e = (err && typeof err === 'object' ? err : {}) as { code?: unknown; message?: unknown; fields?: unknown };
  const code = typeof e.code === 'string' ? e.code : '';
  const message = readable(e.message);

  if (e.fields && typeof e.fields === 'object') {
    const fields: FieldErrors = {};
    let general = '';
    for (const [key, reason] of Object.entries(e.fields as Record<string, unknown>)) {
      const text = readable(reason) || GENERIC_VALIDATION;
      const target = key === '_' ? '' : (codeToField[key] ?? key);
      if (target) fields[target] = fields[target] ?? text;
      else general = text;
    }
    if (Object.keys(fields).length > 0 || general) return { fields, general };
  }

  const field = codeToField[code];
  if (field) {
    return { fields: { [field]: message || CODE_MESSAGES[code] || GENERIC_FIELD }, general: '' };
  }

  return {
    fields: {},
    general: message || CODE_MESSAGES[code] || (code === 'VALIDATION_ERROR' ? GENERIC_VALIDATION : fallback),
  };
}

/** The input carrying `name`; for a MUI Select, its visible control rather than the hidden native input. */
function fieldElement(name: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector<HTMLElement>(`[name="${name.replace(/["\\]/g, '\\$&')}"]`);
  if (!el) return null;
  if (el.getAttribute('aria-hidden') === 'true') {
    return el.parentElement?.querySelector<HTMLElement>('[role="combobox"],[role="button"]') ?? el;
  }
  return el;
}

/** The first errored field in the order the form shows them (falling back to insertion order). */
export function firstErrorField(fields: FieldErrors): string | undefined {
  const names = Object.keys(fields).filter((k) => fields[k]);
  if (names.length === 0) return undefined;
  if (typeof document !== 'undefined') {
    for (const el of Array.from(document.querySelectorAll<HTMLElement>('[name]'))) {
      const n = el.getAttribute('name');
      if (n && fields[n]) return n;
    }
  }
  return names[0];
}

function focusField(name: string | undefined): void {
  if (!name) return;
  fieldElement(name)?.focus();
}

function prune(fields: FieldErrors): FieldErrors {
  const out: FieldErrors = {};
  for (const [k, v] of Object.entries(fields)) if (v) out[k] = v;
  return out;
}

/**
 * Per-form error state. `setErrors` / `setField` also focus the (first)
 * errored input, so a failed submit lands the cursor where the fix goes;
 * `apply` does the same for a rejected request and returns what is left for
 * the page-level Alert. `bind(name)` is spread onto a TextField: it sets the
 * `name`, the red outline, the hint (or the default helper text) and clears
 * the hint as soon as the person types again.
 */
export function useFieldErrors() {
  const [errors, setErrorsState] = useState<FieldErrors>({});

  const setErrors = useCallback((next: FieldErrors) => {
    const clean = prune(next);
    setErrorsState(clean);
    focusField(firstErrorField(clean));
  }, []);

  const setField = useCallback((name: string, message: string) => {
    setErrorsState((prev) => ({ ...prev, [name]: message }));
    focusField(name);
  }, []);

  const clearField = useCallback((name: string) => {
    setErrorsState((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const clear = useCallback(() => setErrorsState({}), []);

  const apply = useCallback((err: unknown, codeToField?: Record<string, string>, fallback?: string): string => {
    const { fields, general } = fieldErrorsFromApi(err, codeToField, fallback);
    // A field this form has no input for would vanish; say it at the top instead.
    const shown: FieldErrors = {};
    const unshown: string[] = [];
    for (const [name, text] of Object.entries(fields)) {
      if (fieldElement(name)) shown[name] = text;
      else unshown.push(text);
    }
    setErrorsState(shown);
    focusField(firstErrorField(shown));
    return [general, ...unshown].filter(Boolean).join('；');
  }, []);

  const bind = useCallback(
    (name: string, defaultHelper?: ReactNode) => ({
      name,
      error: Boolean(errors[name]),
      helperText: errors[name] ?? defaultHelper,
      onInput: () => clearField(name),
    }),
    [errors, clearField],
  );

  return {
    errors,
    setErrors,
    setField,
    clear,
    clearField,
    apply,
    hasErrors: Object.keys(errors).length > 0,
    bind,
  };
}
